import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import '../../styles/components/course-lessons/course-lessons-page.scss';
import ICard from "../../models/view/common/ICard";
import Card from "../common/Card2";
import {NavLink} from "react-router-dom";
import {appPagePaths} from "../../config/config";
import {
  cardClicked,
  useCourseLessonsPageViewModel
} from "../../services/courseLessons/courseLessons";
import useScroll from "../common/hooks/useScroll";
import LoadingAnimation2 from "../loadingAnimation/LoadingAnimation2";
import {FontIcon} from "../common/FontIcon";
import TabbedNavigation from "../tabbedNavigation/TabbedNavigation";
import {CourseOverview} from "../courseOverview/CourseOverview";
import {courseViewedOnDeviceRepository} from "../../repositories/courses/course-viewed-on-device-repository";
import ILessonDetail from "../../models/view/common/ILessonDetail";
import {useParams} from "react-router-dom";
import {ISectionModel} from "../../models/view/bootCampPage/IBootCampPageModel";
import CourseSection from "../bootCamp/CourseSection/CourseSection";

const scrollDataStorageKeyBase = "courseLessonsPageScrollPosition";
export default function CourseLessonsPage() {
  const courseLessonsPageViewModel = useCourseLessonsPageViewModel();
  const areSectionsEnabled = courseLessonsPageViewModel.areSectionsEnabled;
  const elements = areSectionsEnabled
    ? createSections(courseLessonsPageViewModel.courseId, courseLessonsPageViewModel.sections, cardClicked)
    : createCardEls(courseLessonsPageViewModel.showLessonNumbers, courseLessonsPageViewModel.courseLessonCards, cardClicked);

  const loadingEl = courseLessonsPageViewModel.isLoading ? <LoadingAnimation2/> : null;
  const scrollableContentRef = useRef<HTMLDivElement>(document.createElement('div'));
  const scrollDataStorageKey = `${scrollDataStorageKeyBase}_${courseLessonsPageViewModel.courseName}`;
  const routerParams = useParams();
  const courseId = parseInt(routerParams.courseId ?? '', 10);

  const [isCourseOverviewOpen, setIsCourseOverviewOpen] = useState<boolean>(false);

  // Manage marking of courses as having been viewed on the device
  // Delay display of the overview panel slightly so user is more likely to see the transition
  useEffect(() => {
    const timer = setTimeout(() => {
      const hasOverviewForCourseBeenDisplayedOnDevice = courseViewedOnDeviceRepository.hasOverviewBeenDisplayed(courseId);
      if (!hasOverviewForCourseBeenDisplayedOnDevice) {
        setIsCourseOverviewOpen(true);
        courseViewedOnDeviceRepository.setDisplayedCourse(courseId);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [setIsCourseOverviewOpen, courseId]);

  // Scroll to the top of the view when the panel is opened
  useLayoutEffect(() => {
    if (isCourseOverviewOpen) {
      scrollableContentRef.current.scrollTo({top: 0, behavior: 'smooth'});
    }
  }, [isCourseOverviewOpen, scrollableContentRef]);

  useScroll(scrollableContentRef, scrollDataStorageKey);

  let detailsOfLessons: ILessonDetail[] = [];

  courseLessonsPageViewModel.sections.forEach(function(s) {
    const sectionId = s.id;

    const sectionDetailsOfLessons = s.cards.map(({isComplete, difficultyCategory, number}): ILessonDetail => {
      return {
        sectionId,
        isComplete,
        difficultyCategory,
        number,
      };
    });

    detailsOfLessons = [...detailsOfLessons, ...sectionDetailsOfLessons];
  });

  const onClickOverviewToggleHandler = useCallback(
    () => setIsCourseOverviewOpen(current => !current),
    [setIsCourseOverviewOpen],
  );

  const dynamicPanelToggleLabel = isCourseOverviewOpen ? 'Hide overview' : 'Show overview';

  return (
    <div className="course-lessons-page">
      {loadingEl}
      <header className="header">
        <div className="back-to-courses">
          <NavLink to={appPagePaths.coursesPage}>
            <FontIcon icon={"icon-arrow-left"}/>
            <span>Courses</span>
          </NavLink>
        </div>
        <h1>{courseLessonsPageViewModel.courseName}</h1>
        <button
          className={`overview-toggle ${isCourseOverviewOpen ? `active` : ``}`}
          onClick={onClickOverviewToggleHandler}
          aria-label={dynamicPanelToggleLabel}
          title={dynamicPanelToggleLabel}
        >
          {isCourseOverviewOpen ? 'Close Overview' : 'Course Overview'}
          <FontIcon icon={"icon-angle-down"} />
        </button>
      </header>
      <div className="scrollable-content" ref={scrollableContentRef}>
        <CourseOverview
          courseId={courseId}
          isOverviewOpen={isCourseOverviewOpen}
          detailsOfLessons={detailsOfLessons}
        />
        { areSectionsEnabled
          ? <div className="courseSections">{elements}</div>
          : <div className="cards">{elements}</div>
        }
      </div>
      <footer className="footer">
        <TabbedNavigation/>
      </footer>
    </div>
  );
}

function createSections(courseId: number, sections: ISectionModel[], cardClicked: (card: ICard) => void) {
  return sections.map((s, i) => <CourseSection
    courseId={courseId}
    sectionNumber={i + 1}
    key={`course-${courseId}-section-${i}`}
    sectionModel={s}
    onCardClick={(c) => cardClicked(c)}
    quantityOfNewSongs={s.quantityOfNewSongs}
    sectionId={s.id}
  />);
}

function createCardEls(showLessonNumbers: boolean, cardModels: ICard[],  onClick: (c: ICard) => void){
  return cardModels.map(c => createCardEl(c, showLessonNumbers, onClick));
}
function createCardEl(cardModel: ICard, showLessonNumbers: boolean, onClick: (c: ICard) => void){
  return <Card key={`card_${cardModel.number}`} cardModel={cardModel} onClick={()=> onClick(cardModel)} showLessonNumbers={showLessonNumbers}/>
}
